import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, XCircle, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import useTheme from "@/utils/useTheme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  useTheme();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const gateway = params.get("gateway") || "stripe";
  const presetCredits = parseInt(params.get("credits") || "0");
  const presetBalance = parseInt(params.get("balance") || "0");

  const [state, setState] = useState(
    (gateway === "razorpay" || gateway === "free") ? "paid" : "polling"
  );
  const [balance, setBalance] = useState(presetBalance);
  const [credits, setCredits] = useState(presetCredits);
  const [err, setErr] = useState("");

  const poll = useCallback(async (attempt = 0) => {
    if (attempt >= 8) { setState("timeout"); return; }
    try {
      const { data } = await api.get(`${API}/payments/stripe/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setBalance(data.balance);
        // credits added = total / unit_price (rough)
        setState("paid");
        return;
      }
      if (data.status === "expired") { setState("expired"); return; }
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setErr(formatApiError(e));
      setState("error");
    }
  }, [sessionId]);

  useEffect(() => {
    if (gateway === "stripe" && sessionId) poll(0);
    if (gateway === "razorpay" || gateway === "free") {
      // refresh balance to confirm
      api.get(`${API}/payments/me`).then(({ data }) => setBalance(data.balance)).catch(() => {});
    }
  }, [gateway, sessionId, poll]);

  return (
    <div className="min-h-screen bg-[#0A1029] text-white flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white/[0.03] border border-white/10 rounded-2xl p-8 text-center"
        data-testid="checkout-result-card">
        {state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#4ECDC4] animate-spin mb-4" />
            <h2 className="text-xl font-display mb-1">Confirming payment…</h2>
            <p className="text-white/50 text-sm">Just a moment — Stripe is finishing up.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#4ECDC4]/15 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-9 h-9 text-[#4ECDC4]" />
            </div>
            <h2 className="text-2xl font-display mb-1">Payment successful!</h2>
            <p className="text-white/60 text-sm mb-6">
              Your credits are ready to use.
              {credits ? ` +${credits} credits added.` : ""}
            </p>
            <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 mb-6">
              <p className="text-xs text-white/40 uppercase tracking-wider">Current balance</p>
              <p className="text-3xl font-bold text-[#4ECDC4]" data-testid="success-balance">{balance}</p>
              <p className="text-xs text-white/40">report credit{balance !== 1 ? "s" : ""}</p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" className="flex-1 border-white/15 text-white hover:bg-white/5">
                <Link to="/dashboard" data-testid="success-go-dashboard">Dashboard</Link>
              </Button>
              <Button asChild className="flex-1 bg-[#4ECDC4] hover:bg-[#3db8b0] text-[#0A1029] font-semibold">
                <Link to="/tools/boneah" data-testid="success-go-tool">
                  Choose a tool <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </>
        )}
        {(state === "timeout" || state === "expired" || state === "error") && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-9 h-9 text-red-400" />
            </div>
            <h2 className="text-2xl font-display mb-1">
              {state === "expired" ? "Session expired" : state === "timeout" ? "Taking too long" : "Couldn't confirm payment"}
            </h2>
            <p className="text-white/60 text-sm mb-6">
              {state === "error" ? err : "Please contact support if you were charged. Otherwise, try again."}
            </p>
            <Button onClick={() => nav("/checkout")} className="bg-[#4ECDC4] hover:bg-[#3db8b0] text-[#0A1029]">
              Retry
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default CheckoutSuccessPage;
